// PNG / JPEG C2PA + AI metadata inspect and strip.
// Port of skills/remove-ai-marks/scripts/image_meta.py
import { latin1, startsWith, indexOfBytes, containsAny, concatBytes } from "./bytes";

const PNG_SIG = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
const JPEG_SOI = [0xff, 0xd8];

export const C2PA_MARKERS = [
  "c2pa", "jumb", "c2ma", "contentcredentials", "contentauth", "cai:",
  "http://ns.adobe.com/xmp/InstanceID/",
];

export const AI_META_HINTS = [
  "c2pa", "contentcredentials", "digitalSourceType", "trainedAlgorithmicMedia",
  "compositeWithTrainedAlgorithmicMedia", "algorithmicMedia", "AIGC",
  "AI generated", "Generated by", "Claude", "Anthropic", "OpenAI", "SynthID",
  "Midjourney", "Stable Diffusion", "dcterms:provenance",
];

const ALL_HINTS = [...new Set([...AI_META_HINTS, ...C2PA_MARKERS])];

export function detectImageFormat(u8) {
  if (startsWith(u8, PNG_SIG)) return "png";
  if (startsWith(u8, JPEG_SOI)) return "jpeg";
  return "unknown";
}

const chunkName = (u8, pos) => latin1(u8, pos, pos + 4);

export function inspectPng(u8) {
  const findings = [];
  let hasC2pa = false;
  let hasAi = false;
  if (!startsWith(u8, PNG_SIG)) return { hasC2pa: false, hasAi: false, findings: ["not a PNG"] };
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  let pos = 8;
  while (pos + 8 <= u8.length) {
    const length = dv.getUint32(pos);
    const name = chunkName(u8, pos + 4);
    const start = pos + 8;
    const end = start + length;
    if (end + 4 > u8.length) { findings.push(`truncated chunk ${name}`); break; }
    const payload = u8.subarray(start, end);
    if (["caBX", "juMB", "jumb"].includes(name) || name.startsWith("c2")) {
      hasC2pa = true;
      findings.push(`PNG chunk ${name} (possible C2PA container)`);
    }
    if (["tEXt", "zTXt", "iTXt", "eXIf"].includes(name)) {
      const hits = containsAny(payload, ALL_HINTS);
      if (hits.length) {
        hasAi = true;
        if (hits.some((h) => ["c2pa", "contentcredentials", "jumb"].includes(h.toLowerCase()))) hasC2pa = true;
        findings.push(`PNG ${name}: ${hits.slice(0, 8).join(", ")}`);
      } else {
        findings.push(`PNG ${name} metadata chunk (${length} bytes)`);
      }
    }
    if (name === "IEND") break;
    pos = end + 4;
  }
  const whole = containsAny(u8, C2PA_MARKERS);
  if (whole.length && !hasC2pa) {
    hasC2pa = true;
    findings.push(`byte-scan C2PA markers: ${whole.slice(0, 6).join(", ")}`);
  }
  return { hasC2pa, hasAi: hasAi || hasC2pa, findings };
}

export function inspectJpeg(u8) {
  const findings = [];
  let hasC2pa = false;
  let hasAi = false;
  if (!startsWith(u8, JPEG_SOI)) return { hasC2pa: false, hasAi: false, findings: ["not a JPEG"] };
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  let i = 2;
  const n = u8.length;
  while (i + 4 <= n) {
    if (u8[i] !== 0xff) { i += 1; continue; }
    while (i < n && u8[i] === 0xff) i += 1;
    if (i >= n) break;
    const marker = u8[i];
    i += 1;
    if (marker === 0xd8 || marker === 0xd9) continue;
    if (marker === 0xda) break;
    if (marker >= 0xd0 && marker <= 0xd7) continue;
    if (i + 2 > n) break;
    const seglen = dv.getUint16(i);
    if (seglen < 2 || i + seglen > n) { findings.push(`bad segment length at marker 0x${marker.toString(16)}`); break; }
    const payload = u8.subarray(i + 2, i + seglen);
    i += seglen;
    if (marker === 0xeb) {
      hasC2pa = true;
      findings.push("JPEG APP11 segment (JUMBF/C2PA common)");
    }
    if ([0xe1, 0xe2, 0xed, 0xee, 0xeb].includes(marker)) {
      const hits = containsAny(payload, ALL_HINTS);
      if (hits.length) {
        hasAi = true;
        if (hits.some((h) => ["c2pa", "contentcredentials", "jumb", "contentauth"].includes(h.toLowerCase()))) hasC2pa = true;
        findings.push(`JPEG APP${marker - 0xe0}: ${hits.slice(0, 8).join(", ")}`);
      } else {
        findings.push(`JPEG APP${marker - 0xe0} metadata segment (${seglen} bytes)`);
      }
    }
  }
  const whole = containsAny(u8, C2PA_MARKERS);
  if (whole.length && !hasC2pa) {
    hasC2pa = true;
    findings.push(`byte-scan C2PA markers: ${whole.slice(0, 6).join(", ")}`);
  }
  return { hasC2pa, hasAi: hasAi || hasC2pa, findings };
}

const KEEP_PNG = ["IHDR", "IDAT", "IEND", "PLTE", "tRNS", "gAMA", "pHYs", "sRGB", "cHRM", "iCCP"];

export function stripPng(u8, { stripAllText = true } = {}) {
  if (!startsWith(u8, PNG_SIG)) throw new Error("not PNG");
  const actions = [];
  const parts = [new Uint8Array(PNG_SIG)];
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  let pos = 8;
  while (pos + 8 <= u8.length) {
    const length = dv.getUint32(pos);
    const name = chunkName(u8, pos + 4);
    const start = pos + 8;
    const end = start + length;
    if (end + 4 > u8.length) break;
    const payload = u8.subarray(start, end);
    pos = end + 4;
    let drop = false;
    if (["eXIf", "caBX"].includes(name) || name.startsWith("c2")) {
      drop = true; actions.push(`drop chunk ${name}`);
    } else if (["tEXt", "zTXt", "iTXt"].includes(name)) {
      if (stripAllText || containsAny(payload, ALL_HINTS).length) { drop = true; actions.push(`drop chunk ${name}`); }
    } else if (!KEEP_PNG.includes(name) && containsAny(payload, C2PA_MARKERS).length) {
      drop = true; actions.push(`drop chunk ${name} (C2PA marker in payload)`);
    }
    if (!drop) parts.push(u8.slice(start - 8, end + 4));
    if (name === "IEND") break;
  }
  if (!actions.length) actions.push("no PNG metadata chunks removed (already clean)");
  return { bytes: concatBytes(parts), actions };
}

export function stripJpeg(u8, { stripAllApp = true } = {}) {
  if (!startsWith(u8, JPEG_SOI)) throw new Error("not JPEG");
  const actions = [];
  const parts = [new Uint8Array(JPEG_SOI)];
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  let i = 2;
  const n = u8.length;
  while (i < n) {
    if (u8[i] !== 0xff) { parts.push(u8.slice(i)); actions.push("copied remainder after non-marker byte"); break; }
    while (i < n && u8[i] === 0xff) i += 1;
    if (i >= n) break;
    const marker = u8[i];
    i += 1;
    if (marker === 0xd9) { parts.push(new Uint8Array([0xff, 0xd9])); break; }
    if (marker === 0xd8) continue;
    if (marker >= 0xd0 && marker <= 0xd7) { parts.push(new Uint8Array([0xff, marker])); continue; }
    if (marker === 0xda) {
      parts.push(new Uint8Array([0xff, 0xda]));
      parts.push(u8.slice(i));
      actions.push("preserved entropy-coded scan (SOS to EOF)");
      break;
    }
    if (i + 2 > n) break;
    const seglen = dv.getUint16(i);
    if (seglen < 2 || i + seglen > n) { parts.push(u8.slice(i - 2)); actions.push("truncated segment; copied remainder"); break; }
    const payload = u8.subarray(i + 2, i + seglen);
    const nextI = i + seglen;
    let keep = false;
    let drop = false;
    if (marker >= 0xe0 && marker <= 0xef) {
      if (marker === 0xeb) { drop = true; actions.push("drop APP11 (C2PA/JUMBF)"); }
      else if (stripAllApp && marker !== 0xe0) { drop = true; actions.push(`drop APP${marker - 0xe0}`); }
      else if (containsAny(payload, ALL_HINTS).length) { drop = true; actions.push(`drop APP${marker - 0xe0} (AI/C2PA markers)`); }
      else keep = true;
    } else if (marker === 0xfe) {
      drop = true; actions.push("drop COM comment");
    } else keep = true;
    if (keep && !drop) {
      parts.push(new Uint8Array([0xff, marker]));
      parts.push(u8.slice(i, i + seglen));
    }
    i = nextI;
  }
  if (!actions.length) actions.push("no JPEG APP segments removed");
  return { bytes: concatBytes(parts), actions };
}
